import {announceFromElement} from '@github-ui/aria-live'
import {reportError} from '@github-ui/failbot'
import {on} from 'delegated-events'

const reachabilityPathTransitions = new WeakMap<HTMLElement, Animation>()
// The visible path lags the selected path during fade-out, so rapid clicks use this target.
const reachabilitySelectedPaths = new WeakMap<HTMLElement, HTMLElement>()
interface ReachabilitySnippetTransition {
  animation: Animation
  content: HTMLElement
  originalOverflow: string
  originalOverflowPriority: string
  targetOpen: boolean
}
const reachabilitySnippetTransitions = new WeakMap<HTMLDetailsElement, ReachabilitySnippetTransition>()

function isReachabilityPathMenu(menu: Element): boolean {
  for (const item of menu.querySelectorAll<HTMLElement>('.js-scanning-code-path-menu-item')) {
    const pathId = item.getAttribute('aria-controls')
    const targetId = item.getAttribute('data-target-code-path')
    // Rails path ids retain this prefix even when the target element is missing.
    if (pathId?.startsWith('reachability-call-paths-') || targetId?.startsWith('reachability-call-paths-')) return true
    if (pathId && document.getElementById(pathId)?.querySelector('.dependabot-reachability-path')) return true
  }
  return false
}

function reserveReachabilityPathHeight(container: HTMLElement, currentPath: HTMLElement, nextPath: HTMLElement) {
  const currentHeight = container.getBoundingClientRect().height
  const currentWidth = currentPath.getBoundingClientRect().width
  const {position, visibility, width} = nextPath.style
  const wasHidden = nextPath.hidden

  // Measure the hidden path outside the layout before changing the visible path.
  nextPath.style.position = 'absolute'
  nextPath.style.visibility = 'hidden'
  nextPath.style.width = `${currentWidth}px`
  nextPath.hidden = false
  // This one layout read reserves the new path's space before the fade.
  // eslint-disable-next-line @github-ui/web-vitals/no-layout-read-after-write
  const nextHeight = nextPath.getBoundingClientRect().height
  nextPath.hidden = wasHidden
  nextPath.style.position = position
  nextPath.style.visibility = visibility
  nextPath.style.width = width

  container.style.minHeight = `${Math.max(currentHeight, nextHeight)}px`
}

function copyInput(inputToCopy: HTMLInputElement): HTMLInputElement {
  const input = document.createElement('input')
  input.setAttribute('type', 'hidden')
  input.setAttribute('name', inputToCopy.name)
  input.setAttribute('value', inputToCopy.value)
  return input
}

// Add the checked alerts to all the bulk action forms
function copyCheckedAlertsToBulkActionForms(container: Element) {
  let disableReopenButton = false
  const fixedAlertNumbersElements = container.querySelectorAll<HTMLElement>('.js-scanning-fixed-alert-numbers')
  const fixedAlertNumbers: number[] =
    fixedAlertNumbersElements.length !== 0
      ? /* eslint-disable-next-line @typescript-eslint/no-non-null-assertion */
        JSON.parse(fixedAlertNumbersElements[0]!.getAttribute('data-numbers') || '[]')
      : []

  for (const items of container.querySelectorAll<HTMLElement>('.js-scanning-bulk-action-items')) {
    // Remove any previous alert elements from the form
    items.textContent = ''

    // Copy the checked items to the form
    const checkedAlerts = container.querySelectorAll<HTMLInputElement>(
      '.js-scanning-alert-list [data-check-all-item]:checked',
    )

    const alertNumbersElement = document.getElementById('code-scanning-selected-alert-numbers')
    if (alertNumbersElement) {
      const alertNumbers = []

      for (const checked of checkedAlerts) {
        if (fixedAlertNumbers.indexOf(parseInt(checked.value)) === -1) {
          alertNumbers.push(parseInt(checked.value))
        }
      }
      alertNumbersElement.textContent = JSON.stringify(alertNumbers)
      alertNumbersElement.dispatchEvent(new Event('change'))
    }

    for (const checked of checkedAlerts) {
      // If any of the checked alerts are fixed, then disable the reopen button
      // as it only works for manually resolved alerts
      disableReopenButton ||= fixedAlertNumbers.indexOf(parseInt(checked.value)) !== -1
      items.appendChild(copyInput(checked))
    }
  }

  for (const reopenButton of container.querySelectorAll<HTMLButtonElement>('.js-scanning-reopen-button')) {
    reopenButton.hidden = disableReopenButton
  }
  for (const reopenButtonDisabled of container.querySelectorAll<HTMLButtonElement>(
    '.js-scanning-reopen-button-disabled',
  )) {
    reopenButtonDisabled.hidden = !disableReopenButton
  }
}

on('click', '.js-scanning-reopen-button-disabled', function (event: Event) {
  // Prevent the button from being clicked (we don't use disabled="true" because we want it to be focus-able)
  event.preventDefault()
})

on('change', '.js-scanning-alert-check', function ({currentTarget}) {
  /* eslint-disable-next-line @typescript-eslint/no-non-null-assertion */
  const container = currentTarget.closest<HTMLElement>('.js-scanning-alert-list')!
  const checked = container.querySelector('.js-scanning-alert-check:checked') != null
  const filterOptions = container.querySelector<HTMLElement>('.js-scanning-filter-options')
  const bulkActions = container.querySelector<HTMLElement>('.js-scanning-alert-bulk-actions')
  const selectionStatus = bulkActions?.querySelector<HTMLElement>('[role=status]')
  const alertLinks = container.querySelector<HTMLElement>('.js-scanning-alert-links')

  if (filterOptions) {
    filterOptions.hidden = checked
  }
  if (bulkActions) {
    const bulkActionsChangingVisibility = bulkActions.hidden === checked
    bulkActions.hidden = !checked

    // Tell screenreaders that there's new status information available
    // (how many items are selected).
    // This is only necessary when the bulk actions UI is changing visibility as
    // otherwise the screenreader will notice the update to the live region that's
    // already visible. Note that VoiceOver on MacOS will announce the information
    // without this announceFromElement call, but
    // https://github.com/github/accessibility/blob/6aac4f4b3d25013274d39c2cdf1b4be43570eece/docs/coaching-recommendations/toast-flash-banner/accessible-banner-prototype.md#challenges-with-dynamically-inserted-live-region
    // suggests this shouldn't be relied on for screenreaders in general.
    if (bulkActionsChangingVisibility && selectionStatus) {
      // If the bulk actions are now hidden, it'll announce "0 selected", which is helpful clarification
      announceFromElement(selectionStatus)
    }
  }
  if (alertLinks) {
    alertLinks.style.visibility = checked ? 'hidden' : 'visible'
  }
  copyCheckedAlertsToBulkActionForms(container)
})

function switchReachabilityCodePath(control: Element, targetCodePath: string, menu?: Element): boolean {
  const nextPath = document.getElementById(targetCodePath)
  const pathsContainer = nextPath?.parentElement
  if (
    !nextPath?.matches('.js-scanning-code-path') ||
    !nextPath.querySelector('.dependabot-reachability-path') ||
    nextPath.getAttribute('data-code-path') !== targetCodePath ||
    (menu && control.getAttribute('aria-controls') !== targetCodePath) ||
    !pathsContainer
  ) {
    reportError(new Error('Invalid Dependabot reachability code path target or control'))
    return false
  }

  const codePaths = pathsContainer.querySelectorAll<HTMLElement>(':scope > .js-scanning-code-path')
  const currentPath = pathsContainer.querySelector<HTMLElement>(':scope > .js-scanning-code-path:not([hidden])')
  reachabilityPathTransitions.get(pathsContainer)?.cancel()
  reachabilityPathTransitions.delete(pathsContainer)
  reachabilitySelectedPaths.set(pathsContainer, nextPath)

  if (menu) {
    for (const pathButton of menu.querySelectorAll<HTMLElement>('.js-scanning-code-path-menu-item')) {
      pathButton.ariaChecked = pathButton === control ? 'true' : 'false'
    }
  }

  if (currentPath === nextPath) return true

  const showNextPath = () => {
    for (const codePath of codePaths) {
      codePath.hidden = codePath !== nextPath
    }
  }

  if (!currentPath) {
    reportError(new Error('Missing active Dependabot reachability code path'))
    showNextPath()
    return true
  }
  reserveReachabilityPathHeight(pathsContainer, currentPath, nextPath)

  if (window.matchMedia('(prefers-reduced-motion: reduce)').matches || !currentPath.animate) {
    showNextPath()
    return true
  }

  const fadeOut = currentPath.animate([{opacity: 1}, {opacity: 0}], {
    duration: 75,
    easing: 'ease-in',
    fill: 'forwards',
  })
  reachabilityPathTransitions.set(pathsContainer, fadeOut)
  fadeOut.onfinish = () => {
    if (reachabilityPathTransitions.get(pathsContainer) !== fadeOut) {
      fadeOut.cancel()
      return
    }
    showNextPath()
    fadeOut.cancel()

    const fadeIn = nextPath.animate([{opacity: 0}, {opacity: 1}], {duration: 125, easing: 'ease-out'})
    reachabilityPathTransitions.set(pathsContainer, fadeIn)
    fadeIn.onfinish = () => {
      if (reachabilityPathTransitions.get(pathsContainer) === fadeIn) {
        reachabilityPathTransitions.delete(pathsContainer)
      }
    }
  }
  return true
}

function completeReachabilitySnippetTransition(details: HTMLDetailsElement, transition: ReachabilitySnippetTransition) {
  if (reachabilitySnippetTransitions.get(details) !== transition) return
  reachabilitySnippetTransitions.delete(details)
  details.open = transition.targetOpen
  transition.content.style.setProperty('overflow', transition.originalOverflow, transition.originalOverflowPriority)
  transition.animation.cancel()
}

on('click', '.dependabot-reachability-path details.js-dependabot-reachability-snippet > summary', function (event) {
  const details = event.currentTarget.parentElement
  if (!(details instanceof HTMLDetailsElement) || event.defaultPrevented) return
  if (event.target instanceof Element && event.target.closest('a, button, input, select, textarea')) return

  const content = details.querySelector<HTMLElement>(':scope > .js-dependabot-reachability-snippet-content')
  if (!content) {
    reportError(new Error('Missing Dependabot reachability snippet content'))
    return
  }

  const transition = reachabilitySnippetTransitions.get(details)
  if (transition) {
    event.preventDefault()
    transition.targetOpen = !transition.targetOpen
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
      completeReachabilitySnippetTransition(details, transition)
    } else {
      transition.animation.reverse()
    }
    return
  }

  if (!content.animate || window.matchMedia('(prefers-reduced-motion: reduce)').matches) return

  // Native details hides its content before a toggle handler can animate the close.
  event.preventDefault()
  const opening = !details.open
  if (opening) details.open = true
  const expandedHeight = getComputedStyle(content).height
  const originalOverflow = content.style.overflow
  const originalOverflowPriority = content.style.getPropertyPriority('overflow')
  // Primer's overflow-auto is !important, so a normal inline override
  // cannot hide its scrollbar.
  content.style.setProperty('overflow', 'hidden', 'important')
  const animation = content.animate(
    [
      {height: '0px', opacity: 0},
      {height: expandedHeight, opacity: 1},
    ],
    {
      duration: 150,
      direction: opening ? 'normal' : 'reverse',
      easing: 'ease-in-out',
      fill: 'both',
    },
  )
  const nextTransition = {animation, content, originalOverflow, originalOverflowPriority, targetOpen: opening}
  reachabilitySnippetTransitions.set(details, nextTransition)
  animation.onfinish = () => {
    if (animation.playState === 'finished') completeReachabilitySnippetTransition(details, nextTransition)
  }
  animation.oncancel = () => completeReachabilitySnippetTransition(details, nextTransition)
})

on(
  'click',
  '.js-scanning-code-path-menu-item',
  function (event) {
    if (event.currentTarget === null || !(event.currentTarget instanceof Element)) return
    const targetCodePath = event.currentTarget.getAttribute('data-target-code-path')
    const menu = event.currentTarget.closest('action-menu')
    if (menu && isReachabilityPathMenu(menu)) {
      if (!targetCodePath) {
        event.stopPropagation()
        reportError(new Error('Invalid Dependabot reachability code path control'))
        return
      }
      if (!switchReachabilityCodePath(event.currentTarget, targetCodePath, menu)) event.stopPropagation()
      return
    }
    for (const pathButton of document.querySelectorAll<HTMLElement>('.js-scanning-code-path-menu-item')) {
      pathButton.ariaChecked = 'false'
    }
    event.currentTarget.ariaChecked = 'true'
    for (const codePath of document.querySelectorAll<HTMLElement>('.js-scanning-code-path')) {
      codePath.hidden = targetCodePath !== codePath.getAttribute('data-code-path')
    }
  },
  {capture: true},
)

on('click', '.js-dependabot-reachability-path-prev, .js-dependabot-reachability-path-next', function ({currentTarget}) {
  if (!(currentTarget instanceof HTMLButtonElement) || currentTarget.disabled) return

  const navigation = currentTarget.closest<HTMLElement>('.js-dependabot-reachability-path-navigation')
  const dialog = navigation?.closest('dialog')
  const previous = navigation?.querySelector<HTMLButtonElement>('.js-dependabot-reachability-path-prev')
  const next = navigation?.querySelector<HTMLButtonElement>('.js-dependabot-reachability-path-next')
  const label = navigation?.querySelector<HTMLElement>('.js-dependabot-reachability-path-label')
  const pathsContainer = dialog
    ?.querySelector('.dependabot-reachability-path')
    ?.closest<HTMLElement>('.js-scanning-code-path')?.parentElement
  const paths = Array.from(pathsContainer?.querySelectorAll<HTMLElement>(':scope > .js-scanning-code-path') ?? [])
  const selectedPath = pathsContainer
    ? (reachabilitySelectedPaths.get(pathsContainer) ?? paths.find(path => !path.hidden))
    : undefined
  const currentIndex = selectedPath ? paths.indexOf(selectedPath) : -1
  const targetIndex = currentIndex + (currentTarget === next ? 1 : -1)
  const targetPath = paths[targetIndex]
  const targetLabel = targetPath?.getAttribute('data-path-label')

  if (
    !dialog ||
    !previous ||
    !next ||
    (currentTarget !== previous && currentTarget !== next) ||
    !label ||
    currentIndex < 0 ||
    !targetPath?.id ||
    !targetLabel ||
    !targetPath.querySelector(':scope > .dependabot-reachability-path')
  ) {
    reportError(new Error('Invalid Dependabot reachability path navigation'))
    return
  }
  if (!switchReachabilityCodePath(currentTarget, targetPath.id)) return

  label.textContent = targetLabel
  const disablePrevious = targetIndex === 0
  const disableNext = targetIndex === paths.length - 1
  if (document.activeElement === currentTarget && (currentTarget === previous ? disablePrevious : disableNext)) {
    const availableButton = currentTarget === previous ? next : previous
    availableButton.focus()
  }
  previous.disabled = disablePrevious
  next.disabled = disableNext
})
